import { useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { fetchAndSetPostMethodData } from '../commonSlice'
import { getEndpointFromConfig } from '../utils/helper'
import { endpointLabels, featureType } from '../utils/enum'
import ImageWithFallback from './ImageWithFallback'
import SectionCardContentLoader from './CardContentLoader/SectionCardContentLoader'

const InsightAndResource = ({ topicId = '', themeId, pageName }) => {
  const { endpointList, apiKeyName, apiKeyValue, featureProps } = useSelector(
    (state) => state.common
  )
  const [data, setData] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const InsightAndResCompRef = useRef(null)
  const { mode, searchURLPublish, searchURLAuthor } = featureProps
  const dispatch = useDispatch()

  useEffect(() => {
    if (!InsightAndResCompRef.current) return

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            if (
              (pageName === featureType.ThemeProfile && themeId) ||
              (pageName === featureType.TopicProfile && themeId && topicId)
            ) {
              setIsLoading(true)
              fetchInsightAndResource()
            }
            observer.unobserve(entry.target)
          }
        })
      },
      {
        root: null,
        rootMargin: '0px',
        threshold: 0.1,
      }
    )

    if (InsightAndResCompRef.current) {
      observer.observe(InsightAndResCompRef.current)
    }

    return () => observer.disconnect()
  }, [topicId, themeId])

  const getSelectedId = () => {
    switch (pageName) {
      case featureType.ThemeProfile:
        return themeId
      case featureType.TopicProfile:
        return topicId
      default:
        return ''
    }
  }

  const getRedirectionUrl = () => {
    let searchUrl = ''
    if (mode === 'publish') {
      searchUrl = searchURLPublish
    } else {
      searchUrl = searchURLAuthor
    }

    return `${searchUrl}?tab=insights-resources&themeAndTopics=${getSelectedId()}`
  }

  const setThemeOrTopicQuery = () => {
    if (pageName === featureType.ThemeProfile) {
      return `theme`
    } else if (pageName === featureType.TopicProfile) {
      return `topic`
    }
    return ''
  }

  const fetchInsightAndResource = async () => {
    try {
      const request = {
        count: true,
        facets: [],
        orderby: '',
        search: '',
        searchFields: '',
        select:
          'subtype,series_description/name,series_description/definition_long,series_description/links, series_description/idno',
        top: 6,
        filter: `type eq 'document' and doc_type eq 'insight-resource' and series_description/topics/any(t3: (t3/id eq '${getSelectedId()}')) and tags/any(t : t/tag_group eq 'feature-${setThemeOrTopicQuery()}-profile' and t/tag eq '${getSelectedId()}')`,
      }
      const headers =
        apiKeyName && apiKeyValue ? { [apiKeyName]: apiKeyValue } : {}
      const metadataResponse = await dispatch(
        fetchAndSetPostMethodData({
          endpointData: {
            endpoint: getEndpointFromConfig(
              endpointList,
              endpointLabels.Metadata
            ).apiEndpoint,
            method: 'POST',
            data: request,
            headers,
          },
          key: `insights&resources`,
        })
      )
      setData(metadataResponse?.payload?.value || [])
      setIsLoading(false)
    } catch (error) {
      setIsLoading(false)
      console.error('Error fetching metadata in Featured data:', error)
    }
  }

  return !isLoading && (!data || data.length === 0) ? null : (
    <div
      className="container responsivegrid padd-b-48 touchui-container aem-GridColumn aem-GridColumn--default--122"
      id="dataAndSurveys"
    >
      <div className="insightsandresource">
        <div className="container responsivegrid aem-GridColumn aem-GridColumn--default--12">
          <div className="aem-Grid">
            <div className="title aem-GridColumn--xl--9 aem-GridColumn--default--9 aem-GridColumn--desktop--9 aem-GridColumn--tablet--9 aem-GridColumn--phone--12 aem-GridColumn--xs--12">
              <div className="cmp-title">
                <h3 ref={InsightAndResCompRef} className="cmp-title__text">
                  Insights & Resources
                </h3>
              </div>
            </div>
            <div className="button view-all-link aem-GridColumn--xl--3 aem-GridColumn--default--3 aem-GridColumn--desktop--3 aem-GridColumn--tablet--3 aem-GridColumn--phone--12 aem-GridColumn--xs--12">
              <a
                className="cmp-button"
                href={getRedirectionUrl()}
                data-customlink="nl:body content"
              >
                <span className="cmp-button__text">VIEW ALL</span>
              </a>
            </div>
          </div>
        </div>
        <div className="container responsivegrid tui_flexcard_wrapper aem-GridColumn aem-GridColumn--default--12">
          {!isLoading ? (
            data.length > 0 &&
            data.map((obj, index) => {
              const { image_uri, uri } = obj.series_description?.links?.[0]
              return (
                <div
                  key={obj.series_description.idno}
                  className="teaser tui__primary_card aem-GridColumn--xs--12 aem-GridColumn--phone--12 aem-GridColumn--tablet--6 aem-GridColumn--desktop--4 aem-GridColumn--default--4"
                >
                  <div className="cmp-teaser">
                    <div className="cmp-teaser__image">
                      <div data-cmp-is="image" className="cmp-image">
                        <ImageWithFallback
                          src={image_uri}
                          alt={obj.series_description.name}
                        />
                      </div>
                    </div>
                    <div className="cmp-teaser__content">
                      <p className="cmp-teaser__pretitle">{obj.subtype}</p>
                      <h3 className="cmp-teaser__title">
                        <a data-customlink="nl:body content" href={uri}>
                          {obj.series_description.name}
                        </a>
                      </h3>
                    </div>
                  </div>
                </div>
              )
            })
          ) : (
            <SectionCardContentLoader />
          )}
        </div>
      </div>
    </div>
  )
}

export default InsightAndResource
